import { useState, useEffect, useCallback } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type { InvitationRulesDTO } from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      'invalid invitation rules': '邀请规则参数无效',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

interface InvitationRuleStepForm {
  title: string;
  description: string;
}

const EMPTY_STEP: InvitationRuleStepForm = {
  title: '',
  description: '',
};

function pickMarketRules(data: InvitationRulesDTO | null, marketId: string) {
  const target = marketId.trim() || 'CN';
  const item = data?.items?.find((entry) => (entry.market_id || 'CN') === target);
  return {
    steps: item?.steps ?? (target === 'CN' ? data?.steps ?? [] : [{ ...EMPTY_STEP }]),
    rules: item?.rules ?? (target === 'CN' ? data?.rules ?? [] : ['']),
  };
}

export function useInvitationRules() {
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<InvitationRulesDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMarketId, setSelectedMarketId] = useState('CN');
  const [steps, setSteps] = useState<InvitationRuleStepForm[]>([]);
  const [rules, setRules] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getInvitationRules();
      setData(result);
      const selected = pickMarketRules(result, selectedMarketId);
      setSteps(selected.steps);
      setRules(selected.rules);
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, [selectedMarketId]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStep = useCallback((index: number, key: keyof InvitationRuleStepForm, value: string) => {
    setSteps((prev) => prev.map((item, idx) => (idx === index ? { ...item, [key]: value } : item)));
  }, []);

  const addStep = useCallback(() => {
    setSteps((prev) => [...prev, { ...EMPTY_STEP }]);
  }, []);

  const removeStep = useCallback((index: number) => {
    setSteps((prev) => prev.filter((_, idx) => idx !== index));
  }, []);

  const updateRule = useCallback((index: number, value: string) => {
    setRules((prev) => prev.map((item, idx) => (idx === index ? value : item)));
  }, []);

  const selectMarket = useCallback((marketId: string) => {
    const nextMarketId = marketId.trim() || 'CN';
    setSelectedMarketId(nextMarketId);
    const selected = pickMarketRules(data, nextMarketId);
    setSteps(selected.steps);
    setRules(selected.rules);
  }, [data]);

  const addRule = useCallback(() => {
    setRules((prev) => [...prev, '']);
  }, []);

  const removeRule = useCallback((index: number) => {
    setRules((prev) => prev.filter((_, idx) => idx !== index));
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await settingsService.updateInvitationRules({ market_id: selectedMarketId, steps, rules });
      await load();
      addToast('success', '邀请规则已保存');
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setSaving(false);
    }
  }, [selectedMarketId, steps, rules, addToast, load]);

  return {
    data,
    loading,
    saving,
    error,
    selectedMarketId,
    steps,
    rules,
    selectMarket,
    updateStep,
    addStep,
    removeStep,
    updateRule,
    addRule,
    removeRule,
    save,
    reload: load,
  };
}
